import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { warnIfTruncated } from '@/lib/warn-if-truncated';

// Shared data layer for autocomplete wrappers. Each wrapper supplies tiny
// closures over its domain API + queryKey factory; this hook handles the
// load strategy, debounce, open/query state, and the selected-detail fetch.
//
//   - 'preload': fetch one full page (up to `preloadLimit`) the first time
//     the popover opens and cache it for 5 min. The Combobox filters in
//     memory. No network on keystrokes. If the catalog outgrows the page,
//     `truncated` is set so the UI can show a "first N of M" footer.
//   - 'lazy': fetch only when the popover opens, with a debounced `q`.
//     Cached for 60 s. One `lazyLimit` page at a time. A page that doesn't
//     cover the server's match count sets `truncated` too — see below.
//   - 'always': like lazy but `staleTime: 0` — every open refetches.
//
// `excludeCount` (typically `excludeIds?.length ?? 0`) is folded into the
// lazy/always limit so that worst-case client-side exclusion filtering
// still surfaces a useful page. Without this, a Skill picker with 15
// already-assigned skills sees the server's 20-item page collapse to 5
// visible items. Preload pulls the full list once, so excludeCount has no
// bearing there — and pushing past the server's max-limit gate would 400.
export type LoadStrategy = 'preload' | 'lazy' | 'always';

/** Fallback ceiling for the lazy/always request size, used when the caller
 *  doesn't declare its endpoint's own cap. The list endpoints reject a `limit`
 *  above their configured maximum, so `lazyLimit` at that cap plus a non-zero
 *  `excludeCount` would ask for one row too many and 400 — an empty dropdown,
 *  exactly the failure a big page size was meant to prevent. Clamp instead:
 *  losing a little exclusion padding at the very top of the range is strictly
 *  better than losing the page. 500 is right for the big catalogs (customers,
 *  users, products); anything lower — departments caps at 200 — MUST pass its
 *  own `maxLimit`, or the clamp lets the overshoot through. */
const DEFAULT_MAX_PAGE = 500;

interface ListResult<T> {
  data: T[];
  /** Pagination metadata, when the underlying endpoint returns it. Used to
   *  detect a truncated page in either mode (see `truncated` below). */
  meta?: { total: number };
}

interface DetailResult<T> {
  data: T;
}

interface Options<T> {
  value: string | null;
  strategy: LoadStrategy;
  fetchList: (opts: { q?: string; limit: number }) => Promise<ListResult<T>>;
  fetchOne: (id: string) => Promise<DetailResult<T>>;
  listKey: (opts: { q?: string; limit: number }) => readonly unknown[];
  detailKey: (id: string) => readonly unknown[];
  excludeCount?: number;
  preloadLimit?: number;
  lazyLimit?: number;
  /** The endpoint's hard `limit` ceiling, when it is lower than
   *  `DEFAULT_MAX_PAGE`. Only the excludeCount padding can push a request past
   *  `lazyLimit`, so this is what keeps that padding from 400ing a picker
   *  whose page size is already the maximum the server accepts. */
  maxLimit?: number;
  debounceMs?: number;
}

export interface EntityAutocomplete<T> {
  items: readonly T[];
  selectedItem: T | null;
  /** A list fetch is in flight. This is `isFetching`, deliberately, not
   *  `isLoading`: lazy/always mode keeps the previous page on screen while a
   *  new `q` is queried (`keepPreviousData`), and `isLoading` is false the
   *  moment any page has been cached for the key — so on every keystroke
   *  after the first, `isLoading` reports "settled" while the results on
   *  screen are stale. Combobox shows it as a spinner in place of the
   *  dropdown caret, over the (still usable) previous rows. */
  loading: boolean;
  /** A page has arrived — possibly EMPTY, possibly the previous one kept by
   *  `keepPreviousData`. Tells "fetched nothing" from "nothing fetched yet",
   *  which `items.length` cannot. */
  hasData: boolean;
  isClient: boolean;
  /** Non-null when the loaded page is smaller than the server's total for
   *  the current request — the catalog outgrew `preloadLimit`, or the lazy
   *  page didn't cover every row matching the current `q`. Render a footer
   *  telling the user only the first `shown` of `total` entries are listed;
   *  without it a capped page presents itself as the complete answer and a
   *  customer that sorts past the cap reads as "not in the system". */
  truncated: { shown: number; total: number } | null;
  /** Why the dropdown's list could not be loaded, or null. Without it a
   *  refused or failed fetch is indistinguishable from an empty catalog — the
   *  picker opens on "No matches" and the user concludes there is nothing to
   *  pick (G15). Render it in the dropdown; see `PickerLoadError`. Null while
   *  an earlier page is still on screen: stale rows are usable, and an error
   *  over a working list would be the more misleading of the two. */
  error: unknown;
  /** True while `value` is being resolved into a displayable item — the
   *  detail fetch is in flight and `selectedItem` is still null. Distinct
   *  from `loading`, which only covers the dropdown's list. A picker mounted
   *  with a value (an edit form) shows nothing but its placeholder for this
   *  whole window, which reads as "empty" rather than "loading"; render a
   *  skeleton on it so a populated required field can't look unset. Always
   *  false when there's no value to resolve. */
  resolvingValue: boolean;
  setQuery: (q: string) => void;
  setOpen: (o: boolean) => void;
}

export function useEntityAutocomplete<T>({
  value,
  strategy,
  fetchList,
  fetchOne,
  listKey,
  detailKey,
  excludeCount = 0,
  preloadLimit = 200,
  lazyLimit = 20,
  maxLimit = DEFAULT_MAX_PAGE,
  debounceMs = 200,
}: Options<T>): EntityAutocomplete<T> {
  const isPreload = strategy === 'preload';
  const [open, setOpen] = useState(false);
  // Sticky "popover has been opened at least once" gate for the preload
  // fetch. Preload pulls a large page (up to `preloadLimit` rows); paying
  // that on MOUNT means a page rendering N pickers fetches N catalogs the
  // user may never look at (the timesheet day list mounts one picker per
  // entry card). Gating on first open keeps the in-memory-filter semantics
  // while fetching only for pickers actually interacted with. Combobox
  // fires `onOpenChange` on mount for `defaultOpen` consumers, so those
  // still fetch immediately.
  const [everOpened, setEverOpened] = useState(false);
  const handleSetOpen = useCallback((o: boolean) => {
    setOpen(o);
    if (o) setEverOpened(true);
  }, []);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const h = setTimeout(() => setDebounced(query), debounceMs);
    return () => clearTimeout(h);
  }, [query, debounceMs]);

  const selected = useQuery({
    queryKey: detailKey(value ?? ''),
    queryFn: () => fetchOne(value as string),
    enabled: Boolean(value),
  });

  const preloadOpts = { limit: preloadLimit };
  const preload = useQuery({
    queryKey: listKey(preloadOpts),
    queryFn: () => fetchList(preloadOpts),
    enabled: isPreload && everOpened,
    staleTime: 5 * 60_000,
  });
  // Preload fetches one capped page and filters in memory. If the catalog
  // has outgrown `preloadLimit` the page is incomplete. We deliberately do
  // NOT degrade to the lazy server-search path here — that showed only
  // `lazyLimit` rows until the user typed, which reads as "entries are
  // missing" (the bug preload exists to fix). Instead the caller surfaces
  // `truncated` (below) as a footer. Also warn in dev: for preload a short
  // page is a misconfiguration, not an expected state. The label is the
  // entity name (first query-key segment); its value is stable across
  // renders.
  const preloadLabel = `${String(listKey(preloadOpts)[0])} autocomplete`;
  useEffect(() => {
    if (isPreload) warnIfTruncated(preload.data, preloadLabel);
  }, [isPreload, preload.data, preloadLabel]);

  const isClient = isPreload;

  const lazyOpts = {
    q: debounced || undefined,
    limit: Math.min(lazyLimit + excludeCount, maxLimit),
  };
  const lazy = useQuery({
    queryKey: listKey(lazyOpts),
    queryFn: () => fetchList(lazyOpts),
    placeholderData: keepPreviousData,
    enabled: !isPreload && open,
    staleTime: strategy === 'always' ? 0 : 60_000,
  });

  // A capped page that doesn't cover `meta.total` is incomplete in EITHER
  // mode, and the honest thing is to say so rather than let the visible rows
  // pass for the whole answer. `meta.total` counts rows matching the same
  // request, so in lazy mode this reflects the active `q` — an unsearched
  // open compares against the full catalog, a search against its matches,
  // and the footer disappears once the query narrows enough to fit.
  const page = isPreload ? preload.data : lazy.data;
  const truncated =
    page?.meta && page.data.length < page.meta.total
      ? { shown: page.data.length, total: page.meta.total }
      : null;

  return {
    items: isClient ? (preload.data?.data ?? []) : (lazy.data?.data ?? []),
    selectedItem: selected.data?.data ?? null,
    loading: isClient ? preload.isFetching : lazy.isFetching,
    hasData: page !== undefined,
    error: page === undefined ? ((isPreload ? preload.error : lazy.error) ?? null) : null,
    isClient,
    truncated,
    // `enabled: Boolean(value)` keeps this false when there's nothing to
    // resolve — a disabled query is pending but not fetching, so isLoading
    // is false. On a fetch error it also goes false with selectedItem still
    // null; the picker falls back to its placeholder, as it did before.
    resolvingValue: selected.isLoading,
    setQuery,
    setOpen: handleSetOpen,
  };
}
